import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchSubmissions } from '../../api/submissionsApi';
import { fetchInvoices } from '../../api/invoicesApi';
import { fetchTickets } from '../../api/supportApi';
import { fetchMembersPage } from '../../api/networkApi';
import '../styles/layout/main.css';
import { avatarBackground, avatarOrDefault, showDefaultAvatarOnError } from '../../utils/avatar';

const pathToTitleMap = {
  '/': 'dashboard',
  '/Speakersummary': 'speaker_profile',
  '/speakermanagement': 'speaker_management',
  '/purchases': 'active_purchases',
  '/author-services': 'author_services',
  '/packages': 'select_package',
  '/submissions': 'manage_submissions',
  '/letters': 'letters_certificates',
  '/invoices': 'invoices_payments',
  '/network': 'my_network',
  '/profile': 'my_profile',
  '/support': 'support',
  '/ResetPassword': 'reset_password',
  '/Publications': 'publications',
  '/DeleteAccount': 'delete_account',
};

const Topbar = ({ pageTitle: propPageTitle, onMenuClick, onPageChange, user }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [searchResults, setSearchResults] = useState({ submissions: [], invoices: [], members: [], tickets: [] });
  const [allData, setAllData] = useState({ submissions: null, invoices: null, members: null, tickets: null });
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [searchCategory, setSearchCategory] = useState('all'); // 'all' | 'submissions' | 'invoices' | 'members' | 'tickets'
  // Members are searched on the server (every member), not filtered from a preloaded page
  const [memberHits, setMemberHits] = useState([]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      setActiveMenu(null);
      if (e.target && !e.target.closest('.hdr-search')) {
        setIsFocused(false);
      }
    };
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  const loadSearchData = async () => {
    if (allData.submissions !== null) return;
    setIsLoadingResults(true);
    try {
      const [subsRes, invsRes, tixRes, memsRes] = await Promise.allSettled([
        fetchSubmissions().catch(() => []),
        fetchInvoices().catch(() => ({ invoices: [] })),
        fetchTickets().catch(() => ({ tickets: [] })),
        Promise.resolve({ members: [] })
      ]);

      setAllData({
        submissions: subsRes.status === 'fulfilled' ? (Array.isArray(subsRes.value) ? subsRes.value : (subsRes.value?.submissions || [])) : [],
        invoices: invsRes.status === 'fulfilled' ? (Array.isArray(invsRes.value) ? invsRes.value : (invsRes.value?.invoices || [])) : [],
        tickets: tixRes.status === 'fulfilled' ? (Array.isArray(tixRes.value) ? tixRes.value : (tixRes.value?.tickets || [])) : [],
        members: memsRes.status === 'fulfilled' ? (Array.isArray(memsRes.value) ? memsRes.value : (memsRes.value?.members || [])) : []
      });
    } catch (err) {
      console.error('Error prefetching search data:', err);
    } finally {
      setIsLoadingResults(false);
    }
  };

  const handleSearchFocus = () => {
    setIsFocused(true);
    setSearchCategory('all');
    loadSearchData();
  };

  useEffect(() => {
    const query = searchQuery.trim();
    if (query.length < 2 || !(searchCategory === 'all' || searchCategory === 'members')) {
      setMemberHits([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchMembersPage(1, query)
        .then((res) => { if (!cancelled) setMemberHits((res?.members || []).slice(0, 5)); })
        .catch(() => { if (!cancelled) setMemberHits([]); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchQuery, searchCategory]);

  useEffect(() => {
    if (!searchQuery.trim() || !allData.submissions) {
      setSearchResults({ submissions: [], invoices: [], members: [], tickets: [] });
      return;
    }

    const query = searchQuery.toLowerCase().trim();

    // Filter submissions
    const filteredSubmissions = (searchCategory === 'all' || searchCategory === 'submissions')
      ? allData.submissions.filter(s => 
          s.title?.toLowerCase().includes(query) ||
          s.id?.toLowerCase().includes(query) ||
          s.authors?.some(author => author?.toLowerCase().includes(query)) ||
          s.keywords?.some(kw => kw?.toLowerCase().includes(query))
        ).slice(0, 5)
      : [];

    // Filter invoices
    const filteredInvoices = (searchCategory === 'all' || searchCategory === 'invoices')
      ? allData.invoices.filter(inv => 
          inv.invoiceNo?.toLowerCase().includes(query) ||
          inv.id?.toLowerCase().includes(query) ||
          inv.desc?.toLowerCase().includes(query) ||
          inv.pkg?.toLowerCase().includes(query)
        ).slice(0, 5)
      : [];

    // Filter tickets
    const filteredTickets = (searchCategory === 'all' || searchCategory === 'tickets')
      ? allData.tickets.filter(tix => 
          tix.id?.toLowerCase().includes(query) ||
          tix.subject?.toLowerCase().includes(query) ||
          tix.category?.toLowerCase().includes(query) ||
          tix.details?.toLowerCase().includes(query)
        ).slice(0, 5)
      : [];

    // Filter members
    const filteredMembers = (searchCategory === 'all' || searchCategory === 'members')
      ? memberHits
      : [];

    setSearchResults({
      submissions: filteredSubmissions,
      invoices: filteredInvoices,
      tickets: filteredTickets,
      members: filteredMembers
    });
  }, [searchQuery, allData, searchCategory, memberHits]);

  const toggleHdrMenu = (menuName, e) => {
    e.stopPropagation();
    setActiveMenu(prev => prev === menuName ? null : menuName);
  };

  // ✅ تعيين اللغة الافتراضية إلى الإنجليزية
  useEffect(() => {
    const savedLang = localStorage.getItem('i18nextLng');
    if (!savedLang) {
      i18n.changeLanguage('en');
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = 'en';
      localStorage.setItem('i18nextLng', 'en');
    } else {
      document.documentElement.dir = savedLang === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = savedLang;
    }
  }, [i18n]);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        const response = await fetch('https://emiratesscholar.com/system/api/user-notifications', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          let list = [];
          if (Array.isArray(data)) {
            list = data;
          } else if (data && typeof data === 'object') {
            list = data.notifications || data.data || data.list || [];
          }
          setNotifications(list);
          setUnreadCount(list.filter(n => n.is_read !== 1).length);
        }
      } catch (err) {
        console.error('Error fetching notifications in Topbar:', err);
      }
    };

    fetchNotifications();

    const handleRefresh = () => fetchNotifications();
    window.addEventListener('refresh-notifications', handleRefresh);
    return () => window.removeEventListener('refresh-notifications', handleRefresh);
  }, []);

  useEffect(() => {
    const mappedTitle = pathToTitleMap[location.pathname];
    if (mappedTitle && onPageChange) {
      onPageChange(mappedTitle);
    }
  }, [location.pathname, onPageChange]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
    localStorage.setItem('i18nextLng', lng);
  };

  const handleLogout = async (e) => {
    if (e) e.preventDefault();
    if (isLoggingOut) return;

    if (!window.confirm(t('confirmLogout', 'Are you sure you want to logout?'))) return;

    setIsLoggingOut(true);

    try {
      const token = localStorage.getItem('auth_token');

      if (token) {
        try {
          await fetch('https://emiratesscholar.com/system/api/logout', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            }
          });
        } catch (apiError) {
          console.warn('⚠️ [Logout] API call failed');
        }
      }

      localStorage.clear();
      navigate('/');

    } catch (error) {
      localStorage.clear();
      sessionStorage.clear();
      navigate('/');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getPageTitle = () => {
    if (propPageTitle) {
      const key = propPageTitle.toLowerCase().replace(/\s+/g, '_');
      const translated = t(key);
      if (translated && translated !== key) return translated;
      return propPageTitle;
    }

    const mappedTitle = pathToTitleMap[location.pathname];
    if (mappedTitle) {
      const translated = t(mappedTitle);
      if (translated && translated !== mappedTitle) return translated;
    }

    return t('portal');
  };

  const handleLinkClick = (path, e) => {
    e.preventDefault();
    setActiveMenu(null);
    navigate(path);
  };

  // Member photo, or the default photo when there is none (or it fails to load)
  const renderAvatar = (url, name, className = "hdr-avatar") => (
    <span className={className} role="img" aria-label={name || ''} style={{ backgroundImage: avatarBackground(url) }}></span>
  );
  const getActiveUser = () => {
    try {
      const storedProfile = localStorage.getItem("user_profile");
      const storedDetails = localStorage.getItem("user_details");
      const storedFull = localStorage.getItem("login_response_full");

      const profile = storedProfile ? JSON.parse(storedProfile) : {};
      const details = storedDetails ? JSON.parse(storedDetails) : {};
      const full = storedFull ? JSON.parse(storedFull) : {};

      // Extract name
      const name = profile?.name || full?.Profile?.name || user?.name || '';
      const lname = profile?.lname || full?.Profile?.lname || user?.lname || '';
      let fullName = [name, lname].filter(Boolean).join(' ').trim();
      if (!fullName || fullName === 'Unknown User') {
        fullName = user?.name || '';
      }
      // Extract email
      const email = profile?.email || details?.email || full?.Profile?.email || user?.email || '';

      // Position under the name: the member's own profession, or nothing (no placeholder,
      // and not the title — "mr" / "dr" is not a position)
      const role = profile?.usr_det?.profession ||
                   details?.profession ||
                   full?.Profile?.usr_det?.profession ||
                   full?.UserDetails?.profession ||
                   user?.role ||
                   '';

      // Extract avatar
      const avatar = details?.imageLink || profile?.usr_det?.imageLink || full?.Profile?.usr_det?.imageLink || user?.avatar || '';

      return {
        name: fullName,
        email,
        role,
        avatar
      };
    } catch (e) {
      return {
        name: user?.name || '',
        email: user?.email || '',
        role: user?.role || '',
        avatar: user?.avatar || ''
      };
    }
  };

  const activeUser = getActiveUser();
  const welcomeText = `${t('welcome_back', 'Welcome back')}, ${activeUser.name} 👋`;

  return (
    <header className="esc-topbar no-print">
      <button className="hdr-burger d-md-none" onClick={onMenuClick} aria-label="Open menu">
        <i className="material-icons">dehaze</i>
      </button>

      {/* Left: greeting + page title */}
      <div className="topbar-brand-mobile">
        <span style={{ fontSize: '12px', fontWeight: '600', color: '#222' }}>{getPageTitle()}</span>
      </div>
      <div className="topbar-heading">
        <span className="topbar-greeting">{welcomeText}</span>
        <span className="topbar-title">{getPageTitle()}</span>
      </div>



      {/* Right: search + language + notifications + account */}
      <div className="hdr-cluster">

        {/* Global search */}
        <label className="hdr-search hide-mob" style={{ position: 'relative' }}>
          <i className="material-icons">search</i>
          <input
            type="text"
            placeholder={t('search_placeholder', 'Search submissions, invoices…')}
            aria-label="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={handleSearchFocus}
          />
          {isFocused && (searchQuery.trim() !== '' || isLoadingResults) && (
            <div className="search-results-dropdown shadow" style={{
              position: 'absolute',
              top: '44px',
              left: 0,
              right: 0,
              minWidth: '400px',
              backgroundColor: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
              zIndex: 1000,
              maxHeight: '450px',
              overflowY: 'auto',
              padding: '12px 0'
            }}>
              {/* Category Filters */}
              <div className="search-filter-pills d-flex gap-1 px-3 pb-2 mb-2 border-bottom" style={{ borderColor: '#f1f5f9', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                {[
                  { id: 'all', label: t('all', 'All') },
                  { id: 'submissions', label: t('submissions', 'Submissions') },
                  { id: 'invoices', label: t('invoices', 'Invoices') },
                  { id: 'members', label: t('members', 'Members') },
                  { id: 'tickets', label: t('tickets', 'Tickets') }
                ].map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setSearchCategory(cat.id);
                    }}
                    style={{
                      background: searchCategory === cat.id ? '#185FA5' : '#f1f5f9',
                      color: searchCategory === cat.id ? '#fff' : '#64748b',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '4px 12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {isLoadingResults ? (
                <div style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                  <div className="spinner-border spinner-border-sm text-primary me-2" role="status" style={{ width: '1rem', height: '1rem' }} />
                  {t('loading_search_data', 'Loading search data...')}
                </div>
              ) : (
                <>
                  {searchResults.submissions.length === 0 &&
                   searchResults.invoices.length === 0 &&
                   searchResults.members.length === 0 &&
                   searchResults.tickets.length === 0 ? (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                      {t('no_matches_found', 'No matches found')}
                    </div>
                  ) : (
                    <>
                      {/* Submissions category */}
                      {searchResults.submissions.length > 0 && (
                        <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', marginBottom: '8px' }}>
                          <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>
                            {t('submissions', 'Submissions')} ({searchResults.submissions.length})
                          </div>
                          {searchResults.submissions.map(sub => (
                            <div
                              key={sub.id}
                              onClick={() => {
                                setIsFocused(false);
                                setSearchQuery('');
                                navigate('/submissions', { state: { submissionId: sub.id } });
                              }}
                              className="search-result-item"
                              style={{
                                padding: '8px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                                transition: 'background-color 0.2s',
                                textAlign: 'left'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{sub.title}</span>
                              <span style={{ fontSize: '11px', color: '#64748b' }}>
                                {t('id', 'ID')}: #{sub.id} • {sub.paperType} • {sub.date}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Invoices category */}
                      {searchResults.invoices.length > 0 && (
                        <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', marginBottom: '8px' }}>
                          <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>
                            {t('invoices', 'Invoices')} ({searchResults.invoices.length})
                          </div>
                          {searchResults.invoices.map(inv => (
                            <div
                              key={inv.id}
                              onClick={() => {
                                setIsFocused(false);
                                setSearchQuery('');
                                navigate(`/invoices?invoice_id=${inv.invoiceNo}`);
                              }}
                              className="search-result-item"
                              style={{
                                padding: '8px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                                transition: 'background-color 0.2s',
                                textAlign: 'left'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{inv.desc}</span>
                              <span style={{ fontSize: '11px', color: '#64748b' }}>
                                {t('invoice_no', 'Invoice No')}: {inv.invoiceNo} • {inv.pkg} • {inv.amount} ({inv.status === 'paid' ? t('paid', 'Paid') : t('pending', 'Pending')})
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Members category */}
                      {searchResults.members.length > 0 && (
                        <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', marginBottom: '8px' }}>
                          <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>
                            {t('members', 'Members')} ({searchResults.members.length})
                          </div>
                          {searchResults.members.map(mem => (
                            <div
                              key={mem.id}
                              onClick={() => {
                                setIsFocused(false);
                                setSearchQuery('');
                                // Open this member's profile, not just the network list
                                navigate('/network', { state: { selectedMember: mem } });
                              }}
                              className="search-result-item"
                              style={{
                                padding: '8px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'background-color 0.2s',
                                textAlign: 'left'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <img src={avatarOrDefault(mem.avatar)} onError={showDefaultAvatarOnError} alt={mem.name} style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} />
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'left' }}>
                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{mem.name}</span>
                                <span style={{ fontSize: '11px', color: '#64748b' }}>
                                  {mem.position || mem.role} • {mem.company || mem.country}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Tickets category */}
                      {searchResults.tickets.length > 0 && (
                        <div style={{ paddingBottom: '4px' }}>
                          <div style={{ padding: '4px 16px', fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>
                            {t('tickets', 'Tickets')} ({searchResults.tickets.length})
                          </div>
                          {searchResults.tickets.map(tix => (
                            <div
                              key={tix.supportId}
                              onClick={() => {
                                setIsFocused(false);
                                setSearchQuery('');
                                navigate(`/support?ticket_id=${tix.supportId}`);
                              }}
                              className="search-result-item"
                              style={{
                                padding: '8px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                                transition: 'background-color 0.2s',
                                textAlign: 'left'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{tix.subject}</span>
                              <span style={{ fontSize: '11px', color: '#64748b' }}>
                                {t('ticket', 'Ticket')}: {tix.id} • {tix.category} • {t('status', 'Status')}: {tix.status === 'active' ? t('active', 'Active') : t('closed', 'Closed')}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </label>

        {/* Language toggle */}
        <div className={`hdr-menu ${activeMenu === 'lang' ? 'open' : ''}`}>
          <button
            className="hdr-icon-btn hdr-lang"
            type="button"
            onClick={(e) => toggleHdrMenu('lang', e)}
            aria-label="Language"
          >
            <span className="material-icons">language</span>
            <span className="hdr-lang-code">{i18n.language === 'ar' ? 'AR' : 'EN'}</span>
            <span className="material-icons hdr-caret">expand_more</span>
          </button>
          <div className="hdr-dropdown hdr-dropdown-sm">
            <button
              type="button"
              onClick={() => { changeLanguage('en'); setActiveMenu(null); }}
              className={`hdr-drop-item ${i18n.language !== 'ar' ? 'active' : ''}`}
              style={{ background: 'none', border: 'none', width: '100%', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <img src="https://flagicons.lipis.dev/flags/4x3/gb.svg" alt="" /> English
            </button>
            <button
              type="button"
              onClick={() => { changeLanguage('ar'); setActiveMenu(null); }}
              className={`hdr-drop-item ${i18n.language === 'ar' ? 'active' : ''}`}
              style={{ background: 'none', border: 'none', width: '100%', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <img src="https://flagicons.lipis.dev/flags/4x3/ae.svg" alt="" /> العربية
            </button>
          </div>
        </div>

        {/* Notifications */}
        <div className={`hdr-menu ${activeMenu === 'notif' ? 'open' : ''}`}>
          <button
            className="hdr-icon-btn"
            type="button"
            onClick={(e) => toggleHdrMenu('notif', e)}
            aria-label="Notifications"
          >
            <span className="material-icons">notifications_none</span>
            {unreadCount > 0 && <span className="hdr-badge">{unreadCount}</span>}
          </button>
          <div className="hdr-dropdown hdr-dropdown-notif">
            <div className="hdr-drop-head">
              <span>{t('notifications', 'Notifications')}</span>
            </div>
            {notifications.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                {t('no_notifications', 'No notifications')}
              </div>
            ) : (
              notifications.slice(0, 3).map((notif) => (
                <a key={notif.id} href="/notifications" onClick={(e) => handleLinkClick('/notifications', e)} className="hdr-notif-item">
                  <span className="hdr-notif-ico" style={{
                    background: notif.is_read === 1 ? '#f1f5f9' : '#e0f2fe',
                    color: notif.is_read === 1 ? '#64748b' : '#185FA5'
                  }}>
                    <span className="material-symbols-outlined">
                      {notif.is_read === 1 ? 'drafts' : 'mail'}
                    </span>
                  </span>
                  <span className="hdr-notif-body">
                    <span className="hdr-notif-title" style={{ fontWeight: notif.is_read !== 1 ? '600' : '400' }}>
                      {notif.activity || 'Notification'}
                    </span>
                    <span className="hdr-notif-sub">{notif.status || ''}</span>
                  </span>
                </a>
              ))
            )}
            <a href="/notifications" onClick={(e) => handleLinkClick('/notifications', e)} className="hdr-drop-foot">
              {t('view_all_notifications', 'View all notifications')}
            </a>
          </div>
        </div>

        {/* Account */}
        <div className={`hdr-menu ${activeMenu === 'account' ? 'open' : ''}`}>
          <button
            className="hdr-avatar-btn"
            type="button"
            onClick={(e) => toggleHdrMenu('account', e)}
            aria-label="Account"
          >
            {renderAvatar(activeUser.avatar, activeUser.name, "hdr-avatar")}
            <span className="hdr-avatar-meta hide-mob">
              <span className="hdr-avatar-name">{activeUser.name}</span>
              {activeUser.role ? <span className="hdr-avatar-role">{activeUser.role}</span> : null}
            </span>
            <span className="material-icons hdr-caret hide-mob">expand_more</span>
          </button>
          <div className="hdr-dropdown hdr-dropdown-account">
            <div className="hdr-account-head">
              {renderAvatar(activeUser.avatar, activeUser.name, "hdr-avatar")}
              <div>
                <div className="hdr-avatar-name">{activeUser.name}</div>
                <div className="hdr-avatar-sub">{activeUser.email}</div>
              </div>
            </div>
            <a href="/profile" onClick={(e) => handleLinkClick('/profile', e)} className="hdr-drop-item">
              <span className="material-icons">account_circle</span> {t('my_profile', 'My Profile')}
            </a>
            <a href="/profile?edit=true" onClick={(e) => handleLinkClick('/profile?edit=true', e)} className="hdr-drop-item">
              <span className="material-icons">edit</span> {t('edit_profile', 'Edit Profile')}
            </a>
            <a href="/ResetPassword" onClick={(e) => handleLinkClick('/ResetPassword', e)} className="hdr-drop-item">
              <span className="material-icons">password</span> {t('reset_password', 'Reset Password')}
            </a>
            <a href="/support" onClick={(e) => handleLinkClick('/support', e)} className="hdr-drop-item">
              <span className="material-icons">support</span> {t('support', 'Support')}
            </a>
            <div className="hdr-drop-divider"></div>
            <a href="#logout" onClick={handleLogout} className="hdr-drop-item danger">
              <span className="material-icons">logout</span> {t('logout', 'Logout')}
            </a>
          </div>
        </div>

      </div>
    </header>
  );
};

export default Topbar;